import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import ManualPropertyForm from './ManualPropertyForm';
import { useLanguage } from '../../../contexts/LanguageContext';

const PropertyInputSection = ({ onAnalyze, isAnalyzing }) => {
  const [inputMethod, setInputMethod] = useState('url');
  const [url, setUrl] = useState('');
  const [file, setFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [urlError, setUrlError] = useState('');
  const [fileError, setFileError] = useState('');
  const fileInputRef = useRef(null);
  const { t } = useLanguage();

  const validateAustralianPropertyUrl = (urlString) => {
    try {
      const urlObj = new URL(urlString);
      const validDomains = ['domain.com.au', 'realestate.com.au', 'www.domain.com.au', 'www.realestate.com.au'];
      return validDomains?.some(domain => urlObj?.hostname?.includes(domain));
    } catch {
      return false;
    }
  };

  const handleUrlChange = (e) => {
    const value = e?.target?.value;
    setUrl(value);
    
    if (value && !validateAustralianPropertyUrl(value)) {
      setUrlError('Please enter a valid Domain.com.au or Realestate.com.au listing URL');
    } else {
      setUrlError('');
    }
  };

  const handleFileSelect = (selectedFile) => {
    if (selectedFile) {
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      const maxSize = 10 * 1024 * 1024;

      if (!validTypes?.includes(selectedFile?.type)) {
        setFileError('Please upload a valid image file (JPG, PNG, or WebP)');
        return;
      }

      if (selectedFile?.size > maxSize) {
        setFileError('File size must be less than 10MB');
        return;
      }

      setFile(selectedFile);
      setFileError('');
    }
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e?.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setIsDragging(false);
    
    const droppedFile = e?.dataTransfer?.files?.[0];
    handleFileSelect(droppedFile);
  };

  const handleFileInputChange = (e) => {
    const selectedFile = e?.target?.files?.[0];
    handleFileSelect(selectedFile);
  };

  const handleAnalyze = () => {
    if (inputMethod === 'url') {
      if (!url) {
        setUrlError('Please enter a property listing URL');
        return;
      }
      if (!validateAustralianPropertyUrl(url)) {
        setUrlError('Please enter a valid Domain.com.au or Realestate.com.au listing URL');
        return;
      }
      onAnalyze({ type: 'url', value: url });
    } else {
      if (!file) {
        setFileError('Please upload a property listing screenshot');
        return;
      }
      onAnalyze({ type: 'file', value: file });
    }
  };

  const handleRemoveFile = () => {
    setFile(null);
    setFileError('');
    if (fileInputRef?.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 md:p-6 lg:p-8 shadow-elevation-md">
      <div className="mb-6">
        <h2 className="text-xl md:text-2xl lg:text-3xl font-semibold text-foreground font-heading mb-2">
          {t('analyzePropertyListing')}
        </h2>
        <p className="text-sm md:text-base text-muted-foreground font-body">
          {t('analyzePropertyDesc')}
        </p>
      </div>
      <div className="flex flex-col sm:flex-row gap-2 mb-6">
        <button
          onClick={() => {
            setInputMethod('url');
            setFile(null);
            setFileError('');
          }}
          className={`flex-1 px-4 py-3 rounded-lg font-medium text-sm transition-base ${
            inputMethod === 'url' ?'bg-primary text-primary-foreground shadow-elevation-sm' :'bg-muted text-muted-foreground hover:bg-muted/80'
          }`}
        >
          <div className="flex items-center justify-center gap-2">
            <Icon name="Link" size={18} color="currentColor" />
            <span>{t('pasteURL')}</span>
          </div>
        </button>
        <button
          onClick={() => {
            setInputMethod('screenshot');
            setUrl('');
            setUrlError('');
          }}
          className={`flex-1 px-4 py-3 rounded-lg font-medium text-sm transition-base ${
            inputMethod === 'screenshot' ?'bg-primary text-primary-foreground shadow-elevation-sm' :'bg-muted text-muted-foreground hover:bg-muted/80'
          }`}
        >
          <div className="flex items-center justify-center gap-2">
            <Icon name="Image" size={18} color="currentColor" />
            <span>{t('uploadScreenshot')}</span>
          </div>
        </button>
        <button
          onClick={() => {
            setInputMethod('manual');
            setUrl('');
            setUrlError('');
            setFile(null);
            setFileError('');
          }}
          className={`flex-1 px-4 py-3 rounded-lg font-medium text-sm transition-base ${
            inputMethod === 'manual' ?'bg-primary text-primary-foreground shadow-elevation-sm' :'bg-muted text-muted-foreground hover:bg-muted/80'
          }`}
        >
          <div className="flex items-center justify-center gap-2">
            <Icon name="ClipboardList" size={18} color="currentColor" />
            <span>{t('enterDetails')}</span>
          </div>
        </button>
      </div>
      {inputMethod === 'url' ? (
        <div className="space-y-4">
          <Input
            type="url"
            label={t('propertyListingURL')}
            placeholder="https://www.domain.com.au/..."
            value={url}
            onChange={handleUrlChange}
            error={urlError}
            description={t('urlDescription')}
          />
          <div className="mt-6">
            <Button
              variant="default"
              size="lg"
              iconName="Sparkles"
              iconPosition="left"
              onClick={handleAnalyze}
              fullWidth
              disabled={isAnalyzing || !url || !!urlError}
            >
              {isAnalyzing ? t('analyzing') : t('analyzeProperty')}
            </Button>
          </div>
        </div>
      ) : inputMethod === 'screenshot' ? (
        <div className="space-y-4">
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-lg p-6 md:p-8 lg:p-10 text-center transition-base ${
              isDragging
                ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
            }`}
          >
            {file ? (
              <div className="space-y-4">
                <div className="flex items-center justify-center">
                  <div className="bg-success/10 p-4 rounded-full">
                    <Icon name="CheckCircle2" size={32} color="var(--color-success)" />
                  </div>
                </div>
                <div>
                  <p className="text-base font-medium text-foreground font-body mb-1">
                    {file?.name}
                  </p>
                  <p className="text-sm text-muted-foreground text-caption">
                    {(file?.size / 1024 / 1024)?.toFixed(2)} MB
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="X"
                  iconPosition="left"
                  onClick={handleRemoveFile}
                >
                  {t('removeFile')}
                </Button>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-center mb-4">
                  <div className="bg-primary/10 p-4 rounded-full">
                    <Icon name="Upload" size={32} color="var(--color-primary)" />
                  </div>
                </div>
                <p className="text-base font-medium text-foreground font-body mb-2">
                  {t('dragDropScreenshot')}
                </p>
                <p className="text-sm text-muted-foreground text-caption mb-4">
                  {t('orClickBrowse')}
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  onChange={handleFileInputChange}
                  className="hidden"
                  id="file-upload"
                />
                <label htmlFor="file-upload">
                  <Button
                    variant="outline"
                    size="default"
                    iconName="Upload"
                    iconPosition="left"
                    as="span"
                    className="cursor-pointer"
                  >
                    {t('uploadScreenshot')}
                  </Button>
                </label>
              </>
            )}
          </div>
          {fileError && (
            <p className="text-sm text-error">{fileError}</p>
          )}
          {file && (
            <div className="mt-6">
              <Button
                variant="default"
                size="lg"
                iconName="Sparkles"
                iconPosition="left"
                onClick={handleAnalyze}
                fullWidth
                disabled={isAnalyzing}
              >
                {isAnalyzing ? t('analyzing') : t('analyzeProperty')}
              </Button>
            </div>
          )}
        </div>
      ) : (
        <ManualPropertyForm onAnalyze={onAnalyze} isLoading={isAnalyzing} />
      )}
    </div>
  );
};

export default PropertyInputSection;